import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, X, ShoppingBag, Package, CheckCircle, XCircle } from 'lucide-react';
import { useCompare } from '../../context/CompareContext';
import { useCart } from '../../context/CartContext';
import { productAPI, IMAGE_BASE } from '../../services/api';

const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

export default function Compare() {
    const { compareIds, removeFromCompare, clearCompare } = useCompare();
    const { addItem } = useCart();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            if (compareIds.length === 0) {
                setProducts([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const res = await productAPI.getAll({ limit: 100 });
                const allProducts = res.data.products || res.data || [];
                const ordered = compareIds.map(id => allProducts.find(p => p.id === id)).filter(Boolean);
                setProducts(ordered);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchProducts();
    }, [compareIds]);

    const getImageUrl = (product) => {
        const img = product.images?.[0];
        if (!img) return null;
        return img.url?.startsWith('http') ? img.url : IMAGE_BASE + img.url;
    };

    if (loading) {
        return <div className="compare-page"><div className="loading"><div className="spinner"></div></div></div>;
    }

    if (products.length === 0) {
        return (
            <div className="compare-page">
                <div className="compare-empty">
                    <BarChart3 size={48} strokeWidth={1} />
                    <h2>Karşılaştırma listeniz boş</h2>
                    <p>Ürün sayfalarından "Karşılaştır" butonuna tıklayarak ürünleri karşılaştırma listesine ekleyebilirsiniz.</p>
                    <Link to="/urunler" className="favorites-login-btn">Ürünleri Keşfet</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="compare-page">
            <div className="compare-header">
                <h1><BarChart3 size={28} /> Ürün Karşılaştırma</h1>
                <button className="wishlist-clear-btn" onClick={clearCompare}>
                    <X size={14} /> Temizle
                </button>
            </div>

            <div className="compare-table-wrapper">
                <table className="compare-table">
                    <thead>
                        <tr>
                            <th className="compare-label-col"></th>
                            {products.map(p => (
                                <th key={p.id} className="compare-product-col">
                                    <button className="compare-remove-btn" onClick={() => removeFromCompare(p.id)} title="Kaldır">
                                        <X size={16} />
                                    </button>
                                    <Link to={`/urun/${p.slug}`} className="compare-product-img">
                                        {getImageUrl(p) ? (
                                            <img src={getImageUrl(p)} alt={p.name} />
                                        ) : (
                                            <Package size={40} strokeWidth={1} />
                                        )}
                                    </Link>
                                    <Link to={`/urun/${p.slug}`} className="compare-product-name">{p.name}</Link>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="compare-label">Fiyat</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">
                                    <span className="compare-price">{formatPrice(parseFloat(p.price || 0))}</span>
                                    {p.comparePrice && (
                                        <span className="compare-old-price">{formatPrice(parseFloat(p.comparePrice))}</span>
                                    )}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label">Kategori</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">{p.category?.name || '-'}</td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label">Stok Durumu</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">
                                    {p.stock > 0 ? (
                                        <span style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                            <CheckCircle size={14} /> Stokta
                                        </span>
                                    ) : (
                                        <span style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                            <XCircle size={14} /> Tükendi
                                        </span>
                                    )}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label">SKU</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">{p.sku || '-'}</td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label">Marka</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">{p.brand || 'Noora Privé'}</td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label">Ücretsiz Kargo</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">
                                    {p.hasFreeShipping ? (
                                        <CheckCircle size={16} color="var(--success)" />
                                    ) : (
                                        <XCircle size={16} color="var(--text-muted)" />
                                    )}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label">Hızlı Teslimat</td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">
                                    {p.hasFastDelivery ? (
                                        <CheckCircle size={16} color="var(--success)" />
                                    ) : (
                                        <XCircle size={16} color="var(--text-muted)" />
                                    )}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td className="compare-label"></td>
                            {products.map(p => (
                                <td key={p.id} className="compare-value">
                                    <button
                                        className="compare-add-cart"
                                        onClick={() => addItem(p.id, null, 1)}
                                        disabled={p.stock <= 0}
                                    >
                                        <ShoppingBag size={15} />
                                        Sepete Ekle
                                    </button>
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
